import { usePage } from "@inertiajs/react"
import { ChevronLeft, Link2, MoreHorizontal, Trash2, UserPlus } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react"
import { toast } from "sonner"
import { NoteShareDialog } from "@/components/notes/note-share-dialog"
import { SHELL_DRAWER_COVER_CLASS } from "@/components/shell/shell-drawer"
import { PEEK_COMPACT_MENU_MIN_WIDTH, PeekIconMenu, PeekMenuItem } from "@/components/platform/company-peek-parts"
import type { MentionItem } from "@/components/prompt-editor/types"
import { TaskMentionsContext, useTaskMentionTexts, type TaskMentionsValue } from "@/components/tasks/task-mentions"
import { TaskDescriptionEditor } from "@/components/tasks/task-description-editor"
import { taskFileMentionItems, TaskFileViewer, TaskFiles } from "@/components/tasks/task-files"
import { AutosizeTextarea } from "@/components/ui/autosize-textarea"
import { Button } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { copyTextWithToast } from "@/lib/clipboard"
import { confirm } from "@/lib/confirm"
import { noteLinkPath } from "@/lib/note-deep-link"
import {
  attachNoteFileFromLibrary,
  createNote,
  deleteNote,
  fetchNote,
  fetchNoteLibraryProjects,
  notifyNotesChanged,
  onNotesChanged,
  removeNoteFile,
  searchNoteMentions,
  updateNote,
  uploadNoteFile,
  type NoteDetail,
  type NoteLibraryProject,
} from "@/lib/notes-api"
import { shellNavUrl } from "@/lib/task-board"
import { readProjectIdFromWorkspaceUrl } from "@/lib/workspace-nav"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { ProjectSummary } from "@/types/workspace"
import { entityMentionItems, mergeTaskMentions, personMentionItem, taskMentionDirectory } from "@/lib/task-mentions"
import { openTaskLink } from "@/lib/task-open-link"
import { cn } from "@/lib/utils"
import type { TaskFile, TaskMentions } from "@/types/tasks"

const EMPTY_MENTIONS: TaskMentions = { people: [], tasks: [], entities: [] }
const TITLE_MAX_LENGTH = 200
const TITLE_CLASS = "text-[17px] leading-6 font-semibold text-ui-text-primary"

function blankNote(): NoteDetail {
  return {
    id: 0,
    title: "",
    body: "",
    access: "author",
    updated_at: "",
    files: [],
    shares: [],
    mentions: EMPTY_MENTIONS,
  }
}

export function NotePanel({
  noteId,
  onBack,
  openShares = false,
  onCreated,
}: {
  noteId: number | null
  onBack: () => void
  openShares?: boolean
  onCreated?: (id: number) => void
}) {
  const { t } = useTranslation("notes")
  const [note, setNote] = useState<NoteDetail | null>(() => (noteId == null ? blankNote() : null))
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [found, setFound] = useState<TaskMentions>(EMPTY_MENTIONS)
  const [viewedFile, setViewedFile] = useState<TaskFile | null>(null)
  const [removingBlobId, setRemovingBlobId] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [sharesOpen, setSharesOpen] = useState(openShares)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(noteId == null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [chosenLibraryProject, setChosenLibraryProject] = useState<{ id: number; frameFormat: string } | null>(null)
  const [libraryProjects, setLibraryProjects] = useState<NoteLibraryProject[] | null>(null)
  const [libraryProjectsLoading, setLibraryProjectsLoading] = useState(false)
  const page = usePage<{ project?: ProjectSummary }>()
  const workspaceUrl = shellNavUrl(page.url)
  const pageProject = page.props.project
  const ambientProjectId = pageProject?.id ?? readProjectIdFromWorkspaceUrl(workspaceUrl)
  const ambientFrameFormat =
    pageProject != null && pageProject.id === ambientProjectId ? pageProject.frame_format : null
  const aliveRef = useRef(true)
  const noteIdRef = useRef(noteId)
  const draftRef = useRef(draft)
  const titleRef = useRef(title)
  const bodyRef = useRef(body)
  const noteRef = useRef(note)
  const onCreatedRef = useRef(onCreated)
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const libraryProjectsRef = useRef<NoteLibraryProject[] | null>(null)
  const libraryLoadRef = useRef(false)
  const texts = useTaskMentionTexts()
  noteIdRef.current = noteId
  draftRef.current = draft
  titleRef.current = title
  bodyRef.current = body
  noteRef.current = note
  onCreatedRef.current = onCreated

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  useEffect(() => {
    if (noteId != null) return
    titleInputRef.current?.focus()
  }, [noteId])

  useEffect(() => {
    if (noteId == null) return
    return onNotesChanged(() => {
      const current = noteRef.current
      if (!current || current.id !== noteId || draftRef.current) return
      const dirty = titleRef.current.trim() !== current.title || bodyRef.current !== current.body
      if (dirty) return
      fetchNote(noteId)
        .then((loaded) => {
          if (!aliveRef.current || noteIdRef.current !== noteId) return
          const latest = noteRef.current
          if (!latest || latest.id !== noteId) return
          if (titleRef.current.trim() !== latest.title || bodyRef.current !== latest.body) return
          setNote(loaded)
          setTitle(loaded.title)
          setBody(loaded.body)
        })
        .catch(() => undefined)
    })
  }, [noteId])

  useEffect(() => {
    if (noteId == null) return
    if (noteRef.current?.id === noteId) return
    let cancelled = false
    setNote(null)
    setFound(EMPTY_MENTIONS)
    setMenuOpen(false)
    setSharesOpen(openShares)
    setSaving(false)
    fetchNote(noteId)
      .then((loaded) => {
        if (cancelled) return
        setNote(loaded)
        setTitle(loaded.title)
        setBody(loaded.body)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        toast.error(error instanceof Error ? error.message : t("loadFailed"))
        onBack()
      })
    return () => {
      cancelled = true
    }
  }, [noteId, onBack, openShares, t])

  const apply = useCallback(
    (next: NoteDetail, fields: { title?: boolean; body?: boolean } = { title: true, body: true }) => {
      setNote(next)
      if (fields.title) setTitle(next.title)
      if (fields.body) setBody(next.body)
      notifyNotesChanged()
    },
    [],
  )

  const mentions = useNoteMentions(note?.mentions ?? EMPTY_MENTIONS, found, setFound, texts.unnamed)
  if (!note) return null

  const editable = note.access === "author" || note.access === "edit"
  const trimmedTitle = title.trim()
  const hasText = trimmedTitle.length > 0 || body.trim().length > 0
  const dirty = trimmedTitle !== note.title || body !== note.body
  const canSave = editable && dirty && hasText && !saving

  const leave = () => {
    if (saving) return
    onBack()
  }

  const back = async () => {
    if (saving) return
    const nextTitle = titleRef.current.trim()
    const nextBody = bodyRef.current
    const wrote = nextTitle.length > 0 || nextBody.trim().length > 0
    const current = noteRef.current
    const changed = current != null && (nextTitle !== current.title || nextBody !== current.body)
    const editableNow = current?.access === "author" || current?.access === "edit"
    const keep = Boolean(current) && editableNow && (draftRef.current ? wrote : changed)
    if (!keep || !current) {
      onBack()
      return
    }

    setSaving(true)
    try {
      if (draftRef.current) {
        await persistDraft()
      } else {
        const saved = await updateNote(current.id, { title: nextTitle, body: nextBody })
        if (!aliveRef.current) return
        apply(saved)
      }
      if (aliveRef.current) onBack()
    } catch (error: unknown) {
      if (!aliveRef.current) return
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    } finally {
      if (aliveRef.current) setSaving(false)
    }
  }

  const persistDraft = async (): Promise<NoteDetail> => {
    const current = noteRef.current
    if (!current) throw new Error("missing note")
    if (!draftRef.current) return current
    const saved = await createNote({ title: titleRef.current.trim(), body: bodyRef.current })
    if (!aliveRef.current) return saved
    draftRef.current = false
    setDraft(false)
    onCreatedRef.current?.(saved.id)
    apply(saved)
    return saved
  }

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      if (draftRef.current) {
        await persistDraft()
        return
      }
      const requestedId = note.id
      const saved = await updateNote(requestedId, { title: trimmedTitle, body })
      if (!aliveRef.current || noteIdRef.current !== requestedId) return
      apply(saved)
    } catch (error: unknown) {
      if (!aliveRef.current) return
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    } finally {
      if (aliveRef.current) setSaving(false)
    }
  }

  const rememberUpload = async (file: File) => {
    const current = await persistDraft()
    apply(await uploadNoteFile(current.id, file), { title: false, body: false })
  }

  const libraryProjectId = ambientProjectId ?? chosenLibraryProject?.id ?? null
  const libraryFrameFormat =
    (ambientProjectId != null ? ambientFrameFormat : chosenLibraryProject?.frameFormat) ?? "16:9"

  const ensureLibraryProjects = () => {
    if (libraryProjectsRef.current != null || libraryLoadRef.current) return
    libraryLoadRef.current = true
    setLibraryProjectsLoading(true)
    fetchNoteLibraryProjects()
      .then((projects) => {
        if (!aliveRef.current) return
        libraryProjectsRef.current = projects
        setLibraryProjects(projects)
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) return
        toast.error(error instanceof Error ? error.message : t("loadFailed"))
      })
      .finally(() => {
        libraryLoadRef.current = false
        if (aliveRef.current) setLibraryProjectsLoading(false)
      })
  }

  const attachFromLibrary = async (source: ShotFrameSource) => {
    const projectId = ambientProjectId ?? chosenLibraryProject?.id
    if (projectId == null) return
    const current = await persistDraft()
    apply(await attachNoteFileFromLibrary(current.id, projectId, source), { title: false, body: false })
  }

  const chooseLibraryProject = (id: number) => {
    const project = libraryProjectsRef.current?.find((item) => item.id === id)
    setChosenLibraryProject({ id, frameFormat: project?.frame_format ?? "16:9" })
  }

  const pasteFiles = async (files: File[]): Promise<MentionItem[]> => {
    const items: MentionItem[] = []
    let current = await persistDraft()
    for (const file of files) {
      current = await uploadNoteFile(current.id, file)
      apply(current, { title: false, body: false })
      const added = current.files[current.files.length - 1]
      if (added) items.push(taskFileMentionItems([added])[0])
    }
    return items
  }

  const remove = async () => {
    setMenuOpen(false)
    if (draftRef.current) {
      onBack()
      return
    }
    if (!(await confirm({ message: t("deleteConfirm"), tone: "danger" }))) return
    try {
      await deleteNote(note.id)
      notifyNotesChanged()
      onBack()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    }
  }

  return (
    <TaskMentionsContext.Provider value={mentions}>
      <div className="flex h-full min-h-0 flex-col">
        <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          <div className="pt-5">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                className="inline-flex h-7 items-center gap-0.5 rounded-lg pr-2 pl-1 text-xs whitespace-nowrap text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                onClick={() => void back()}
              >
                <ChevronLeft className="size-4" />
                {t("backToList")}
              </button>
              {note.id > 0 ? (
                <PeekIconMenu
                  label={t("actions")}
                  icon={<MoreHorizontal className="size-4" />}
                  menuMinWidth={PEEK_COMPACT_MENU_MIN_WIDTH}
                  open={menuOpen}
                  onToggle={() => setMenuOpen((prev) => !prev)}
                  onClose={() => setMenuOpen(false)}
                >
                  <PeekMenuItem
                    icon={<Link2 />}
                    label={t("copyLink")}
                    onSelect={() => {
                      setMenuOpen(false)
                      void copyTextWithToast(
                        `${window.location.origin}${noteLinkPath(note.id)}`,
                        t("linkCopied"),
                        t("copyFailed"),
                      )
                    }}
                  />
                  {note.access === "author" ? (
                    <>
                      <PeekMenuItem
                        icon={<UserPlus />}
                        label={t("openAccess")}
                        onSelect={() => {
                          setMenuOpen(false)
                          setSharesOpen(true)
                        }}
                      />
                      <PeekMenuItem icon={<Trash2 />} label={t("delete")} danger onSelect={() => void remove()} />
                    </>
                  ) : null}
                </PeekIconMenu>
              ) : null}
            </div>
            <div className="mt-4 space-y-1">
              {editable ? (
                <NoteTitleField
                  ref={titleInputRef}
                  autoFocus={draft}
                  value={title}
                  placeholder={t("titlePlaceholder")}
                  ariaLabel={t("title")}
                  onChange={setTitle}
                />
              ) : (
                <h3 className={cn(TITLE_CLASS, "break-words")}>{note.title}</h3>
              )}
              {note.access === "read" ? <p className="text-xs text-ui-text-muted">{t("readOnly")}</p> : null}
            </div>
          </div>
          {editable || body.trim() !== "" ? (
            <div className="mt-4 border-t border-ui-border pt-4">
              <TaskDescriptionEditor
                value={body}
                onChange={setBody}
                onModEnter={() => void save()}
                editable={editable}
                stickyToolbarClassName={editable ? SHELL_DRAWER_COVER_CLASS : undefined}
                variant="inline"
                placeholder={t("descriptionPlaceholder")}
                ariaLabel={t("descriptionPlaceholder")}
                fileMentions={taskFileMentionItems(note.files)}
                onPasteFiles={editable ? pasteFiles : undefined}
                dictation={editable ? { projectId: 0, agentScope: { kind: "company" } } : undefined}
              />
            </div>
          ) : null}
          <div className="mt-4">
            <TaskFiles
              files={note.files}
              projectId={libraryProjectId}
              frameFormat={libraryFrameFormat}
              chooseLibraryProject
              libraryProjects={(libraryProjects ?? []).map((project) => ({
                id: project.id,
                name: project.name,
                frameFormat: project.frame_format,
              }))}
              libraryProjectsLoading={libraryProjectsLoading}
              onLibraryProjectChange={chooseLibraryProject}
              canAttach={editable}
              canRemove={editable}
              removingBlobId={removingBlobId}
              onOpen={setViewedFile}
              onRemove={(file) =>
                void dropFile(
                  note.id,
                  file,
                  setRemovingBlobId,
                  (next) => apply(next, { title: false, body: false }),
                  t("loadFailed"),
                )
              }
              onUpload={rememberUpload}
              onAttachFromLibrary={attachFromLibrary}
              onLibraryOpenChange={(open) => {
                setLibraryOpen(open)
                if (open && ambientProjectId == null && chosenLibraryProject == null) ensureLibraryProjects()
              }}
              dropLabel={t("filesDrop")}
              dropEnabled={viewedFile == null && !sharesOpen && !libraryOpen}
            />
          </div>
        </div>
        {editable ? (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ui-border px-5 py-3">
            <Button type="button" variant="ghost" size="sm" disabled={saving} onClick={leave}>
              {t("cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canSave}
              // Клик не забирает фокус у описания, иначе панель форматирования схлопывается и текст прыгает.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void save()}
            >
              {t("save")}
            </Button>
          </div>
        ) : null}
        <TaskFileViewer file={viewedFile} projectId={null} onClose={() => setViewedFile(null)} />
        <WorkspaceDialog
          open={sharesOpen}
          onOpenChange={setSharesOpen}
          title={t("shares")}
          closeAriaLabel={t("cancel")}
          size="md"
          bodyClassName="p-5 pt-4"
        >
          {sharesOpen ? (
            <NoteShareDialog
              note={note}
              ensureNote={persistDraft}
              onNote={(next) => apply(next, { title: false, body: false })}
            />
          ) : null}
        </WorkspaceDialog>
      </div>
    </TaskMentionsContext.Provider>
  )
}

function NoteTitleField({
  ref,
  value,
  placeholder,
  ariaLabel,
  autoFocus = false,
  onChange,
}: {
  ref: React.Ref<HTMLTextAreaElement>
  value: string
  placeholder: string
  ariaLabel: string
  autoFocus?: boolean
  onChange: (value: string) => void
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return
    event.preventDefault()
  }

  return (
    <AutosizeTextarea
      ref={ref}
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => onChange(event.target.value.replace(/\s*\n\s*/g, " "))}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      aria-label={ariaLabel}
      maxLength={TITLE_MAX_LENGTH}
      minRows={1}
      maxRows={8}
      className={cn(
        TITLE_CLASS,
        "relative rounded-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 sm:text-[17px]",
      )}
    />
  )
}

function useNoteMentions(
  saved: TaskMentions,
  found: TaskMentions,
  setFound: (update: (prev: TaskMentions) => TaskMentions) => void,
  unnamed: string,
): TaskMentionsValue {
  const searchTasks = useCallback(
    async (query: string, signal: AbortSignal) => {
      const loaded = await searchNoteMentions(query, signal)
      setFound((prev) => mergeTaskMentions(prev, loaded))
      return [
        ...loaded.people.map((person) => personMentionItem(person, unnamed)),
        ...entityMentionItems(loaded.entities),
      ]
    },
    [setFound, unnamed],
  )
  const directory = useMemo(
    () => ({ ...taskMentionDirectory(saved, found), entitiesLoaded: true, noteAssetsLoaded: true }),
    [saved, found],
  )
  return useMemo(
    () => ({
      directory,
      people: [],
      entities: [],
      searchTasks,
      ensureTasks: () => undefined,
      openPerson: null,
      openTask: (ref) => openTaskLink(window.location.pathname, ref),
      openEntity: null,
    }),
    [directory, searchTasks],
  )
}

async function dropFile(
  id: number,
  file: TaskFile,
  setRemoving: (blobId: number | null) => void,
  apply: (note: NoteDetail) => void,
  fallback: string,
) {
  setRemoving(file.blob_id)
  try {
    apply(await removeNoteFile(id, file.blob_id))
  } catch (error: unknown) {
    toast.error(error instanceof Error ? error.message : fallback)
  } finally {
    setRemoving(null)
  }
}
